import React, { useRef } from 'react';
import { useCamera } from '@/hooks/useCamera';
import { Button } from '@/components/ui';

interface CameraViewProps {
  onCapture: (imageData: ImageData) => void;
  disabled: boolean;
}

export function CameraView({ onCapture, disabled }: CameraViewProps) {
  const { state, error, videoRef, startCamera, stopCamera, captureFrame } = useCamera();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCapture = () => {
    const frame = captureFrame();
    if (frame) onCapture(frame);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      onCapture(ctx.getImageData(0, 0, img.width, img.height));
    };
    img.src = URL.createObjectURL(file);
  };

  return (
    <div className="relative w-full aspect-[4/3] bg-black rounded-2xl overflow-hidden">
      {state === 'idle' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <Button onClick={startCamera} disabled={disabled}>
            Start Camera
          </Button>
          <span className="text-white/60 text-sm">or</span>
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={disabled}>
            Upload Photo
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
        </div>
      )}

      {state === 'requesting' && (
        <div className="absolute inset-0 flex items-center justify-center text-white/60">
          Requesting camera...
        </div>
      )}

      {state === 'streaming' && (
        <>
          <video ref={videoRef} className="w-full h-full object-contain" playsInline muted />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
            <Button onClick={handleCapture} disabled={disabled}>
              Capture
            </Button>
            <Button variant="ghost" onClick={stopCamera}>
              Stop
            </Button>
          </div>
          <div className="absolute top-4 right-4">
            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>
              Upload
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          </div>
        </>
      )}

      {state === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-red-400 text-sm">{error}</p>
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
            Upload Photo Instead
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
        </div>
      )}
    </div>
  );
}
